/** Stable menuKey / Firestore id for the built-in POS-only Extra item. */
export const POS_EXTRA_MENU_KEY = 'pos-extra'

export const POS_EXTRA_ITEM_ID = 'pos-extra'

export const POS_EXTRA_DISPLAY_NAME = 'Extra'

export interface PosExtraIdentity {
  menuKey?: string
  posOnly?: boolean
  soldOut?: boolean
  soldOutChoices?: unknown
  stockRemaining?: unknown
  soldOutReason?: unknown
  id?: string
  name?: string
  price?: number
  categoryId?: string
  station?: string
  sortIndex?: number
}

/** Built-in Extra only. Does not match Extra-category add-ons (Egg, Omelette, …). */
export function isPosExtraItem(item: PosExtraIdentity | null | undefined): boolean {
  if (!item) return false
  return item.id === POS_EXTRA_ITEM_ID || item.menuKey === POS_EXTRA_MENU_KEY
}

export function isCustomerVisibleMenuItem(item: PosExtraIdentity | null | undefined): boolean {
  if (!item) return true
  if (isPosExtraItem(item)) return false
  return item.posOnly !== true
}

/** Lowest Uncategorized sortIndex so Extra is the first card in that section. */
export function extraUncategorizedSortIndex(
  items: ReadonlyArray<PosExtraIdentity>,
): number {
  let min = 0
  for (const item of items) {
    if (isPosExtraItem(item)) continue
    if (item.categoryId) continue
    const s = item.sortIndex
    if (typeof s === 'number' && Number.isFinite(s)) min = Math.min(min, s)
  }
  return min - 1
}

export function mergePosExtraIntoItems<T extends PosExtraIdentity>(
  items: readonly T[],
  create: (sortIndex: number) => T,
): T[] {
  if (items.some(isPosExtraItem)) return [...items]
  return [...items, create(extraUncategorizedSortIndex(items))]
}

/** Catalog Extra is never a dish: name Extra, price 0, Uncategorized. */
export function lockPosExtraFields(): {
  name: typeof POS_EXTRA_DISPLAY_NAME
  price: 0
  posOnly: true
  menuKey: typeof POS_EXTRA_MENU_KEY
} {
  return {
    name: POS_EXTRA_DISPLAY_NAME,
    price: 0,
    posOnly: true,
    menuKey: POS_EXTRA_MENU_KEY,
  }
}

export function applyPosExtraLock<T extends PosExtraIdentity>(item: T): T {
  if (!isPosExtraItem(item)) return item
  const {
    categoryId: _c,
    station: _s,
    soldOut: _so,
    soldOutChoices: _sc,
    stockRemaining: _sr,
    soldOutReason: _r,
    ...rest
  } = item
  return { ...rest, ...lockPosExtraFields() } as T
}

export function catalogExtraNeedsRelock(item: PosExtraIdentity | null | undefined): boolean {
  if (!item || !isPosExtraItem(item)) return false
  return (
    item.name !== POS_EXTRA_DISPLAY_NAME ||
    item.price !== 0 ||
    item.categoryId != null ||
    item.menuKey !== POS_EXTRA_MENU_KEY ||
    item.posOnly !== true
  )
}

export function defaultPosExtraItemFields(): {
  name: typeof POS_EXTRA_DISPLAY_NAME
  menuKey: typeof POS_EXTRA_MENU_KEY
  posOnly: true
  tags: string[]
  ingredients: string[]
  options: []
  price: number
  description: string
} {
  return {
    name: POS_EXTRA_DISPLAY_NAME,
    menuKey: POS_EXTRA_MENU_KEY,
    posOnly: true,
    tags: [],
    ingredients: [],
    options: [],
    price: 0,
    description: 'Staff-only add-on. Set price and notes when adding to an order.',
  }
}
